import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import { VisitorSearch } from '../../../../../types/visitorSearchTypes';
import { AppDispatch } from '../../../../../store/store';
import {
  setSelectedVisit,
  setSelectedVisitor,
} from '../../../../../store/slices/visitsSlice';
import { Visit } from '../../../../../types/visitTypes';
import { DATE_TIME_FORMAT } from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import { formatFullName } from '../../../../../utils/utils';
import { useTimezoneConverter } from '../../../../../hooks/useTimezoneConverter';

dayjs.extend(utc);
dayjs.extend(timezone);

interface VisitorOption {
  label: string;
  value: string;
  visitId: number;
  visitorId: number;
}

interface SignOutModalProps {
  open: boolean;
  locationIdRef: number;
  onClose: () => void;
  openVisitorsDrawer: () => void;
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[] | null;
}

interface DescribeVisitResponse {
  data?: Visit[] | null;
}

const SignOutModal = ({
  open,
  locationIdRef,
  onClose,
  openVisitorsDrawer,
}: SignOutModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const { timezone: locationTz } = useTimezoneConverter(locationIdRef);

  const { orgId } = getOpParams();
  const { userId } = useIdentityAndUser();

  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const [visitorOptions, setVisitorOptions] = useState<VisitorOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch visitor options
  useEffect(() => {
    const fetchVisitorOptions = async () => {
      setLoading(true);
      try {
        const tz = locationTz || 'UTC';
        const todayStart = dayjs()
          .tz(tz)
          .startOf('day')
          .utc()
          .format(DATE_TIME_FORMAT);
        const todayEnd = dayjs()
          .tz(tz)
          .endOf('day')
          .utc()
          .format(DATE_TIME_FORMAT);

        const visitors = (await heliumReceive('getSearchVisitors', [orgId], {
          queryStringParams: {
            locationId: locationIdRef,
            startDate: todayStart,
            endDate: todayEnd,
          },
        })) as unknown as SearchVisitorsResponse;

        const filteredSignedInVisitors = (visitors.data ?? [])
          .filter((visitor: VisitorSearch) => {
            return visitor.status === STATUS.SIGNED_IN.id;
          })
          .filter((visitor: VisitorSearch) => {
            return hasAllVisitorsWrite || visitor.hostUserId === userId;
          })
          .map((visitor: VisitorSearch) => {
            return {
              label: formatFullName(
                visitor.firstName,
                visitor.middleName,
                visitor.lastName,
              ),
              value: `${visitor.visitId}-${visitor.id}`, // Use visitor.visitId instead of visit.id
              visitId: visitor.visitId, // Use visitId directly instead of the entire visit object
              visitorId: visitor.id,
            };
          });
        setVisitorOptions(filteredSignedInVisitors);
      } catch {
        // Failed to fetch visitor options
      } finally {
        setLoading(false);
      }
    };
    fetchVisitorOptions();
  }, [orgId, userId, hasAllVisitorsWrite, locationIdRef, locationTz]);

  const handleOptionSelect = async (value: string, option: VisitorOption) => {
    const selectedVisit = (await heliumReceive('describeVisit', [
      orgId,
      option.visitId,
    ])) as unknown as DescribeVisitResponse;
    if (selectedVisit.data?.[0]) {
      const visit = selectedVisit.data[0];
      const selectedVisitor = visit.visitors.find(
        (v) => Number(v.id) === Number(option.visitorId),
      );
      dispatch(setSelectedVisit(visit));
      dispatch(setSelectedVisitor(selectedVisitor || null));
    }
    openVisitorsDrawer();
    onClose();
  };

  return (
    <OpModal
      title={t('signOutModal.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
    >
      <OpSpace
        orientation="vertical"
        size="large"
        style={{ display: 'flex', paddingTop: '10px' }}
      >
        <OpSelect
          testId="select-visitors-to-sign-out"
          options={visitorOptions}
          placeholder={t('signOutModal.placeholder')}
          onChange={(value, option) =>
            handleOptionSelect(value as string, option as VisitorOption)
          }
          showSearch
          filterOption={(input, option) =>
            String(option?.label)?.toLowerCase().indexOf(input.toLowerCase()) >=
            0
          }
          loading={loading}
          style={{ width: '100%' }}
        />
      </OpSpace>
    </OpModal>
  );
};

export default SignOutModal;
